class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === 'cart-items') {
          return;
        }
        this.onCartUpdate();
      },
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input =
      this.querySelector(`#Quantity-${id}`) ||
      this.querySelector(`#Drawer-quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        '[min]',
        event.target.dataset.min,
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        '[max]',
        event.target.max,
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        '[step]',
        event.target.step,
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html',
          );
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          // Reinitialize Lucide icons after cart drawer update
          if (typeof lucide !== 'undefined') {
            lucide.createIcons();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html',
          );
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
          // Reinitialize Lucide icons after main cart update
          if (typeof lucide !== 'undefined') {
            lucide.createIcons();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    // Shopify's cart API expects `line` as a number. `this.dataset.index`
    // is always a string (HTML dataset props are), so coerce before sending
    // — otherwise Shopify returns "line parameter is invalid."
    const body = JSON.stringify({
      line: parseInt(line, 10),
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        // Shopify reports cart failures two different ways: an `errors` key,
        // or the `{ status, message, description }` shape used for 4xx
        // responses (e.g. 422 "Only 24 items were added to your cart due to
        // availability."). The latter has no `errors` and no `sections`, so it
        // has to be caught here or the render below blows up.
        const cartError =
          parsedState.errors ||
          (parsedState.status
            ? parsedState.description || parsedState.message
            : null);

        if (cartError) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }

          // The DOM may now be out of sync with cart state — either the line
          // index was stale (from a render that didn't complete) or the server
          // clamped the quantity it accepted. Refresh the cart view first so
          // the next user action is against valid state, then write the
          // message into the freshly rendered line item so it isn't wiped.
          this.renderSectionsFromServer(this.getSectionsToRender()).then(() => {
            this.updateLiveRegions(line, cartError);
          });
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter)
          cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            'is-empty',
            parsedState.item_count === 0,
          );

        // The Section Rendering API is best-effort: `sections` intermittently
        // comes back as null on an otherwise-successful 200, and an individual
        // section can come back null on its own. Reading through it blindly
        // threw, which aborted this whole handler — the cart had already
        // changed server-side but the drawer never re-rendered. Fall back to
        // refetching the cart markup directly instead.
        const sectionsToRender = this.getSectionsToRender();
        const renderedSections = parsedState.sections;
        const sectionsAreRenderable =
          renderedSections &&
          sectionsToRender.every(
            (section) => renderedSections[section.section],
          );

        if (sectionsAreRenderable) {
          sectionsToRender.forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement) return;

            const innerHTML = this.getSectionInnerHTML(
              renderedSections[section.section],
              section.selector,
            );
            if (innerHTML === null) return;

            const elementToReplace =
              sectionElement.querySelector(section.selector) || sectionElement;
            elementToReplace.innerHTML = innerHTML;
          });
        } else {
          this.renderSectionsFromServer(sectionsToRender);
        }

        // Reinitialize Lucide icons after cart update
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = '';
        if (
          items.length === parsedState.items.length &&
          quantityElement &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              '[quantity]',
              updatedValue,
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`),
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector('.drawer__inner-empty'),
            cartDrawerWrapper.querySelector('a'),
          );
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector('.cart-item__name'),
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        console.error('Cart update error:', error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) =>
          overlay.classList.add('hidden'),
        );
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = message;
      }
      // Show/hide error container based on message
      if (message) {
        lineItemError.classList.remove('hidden');
      } else {
        lineItemError.classList.add('hidden');
      }
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  // Fallback for when /cart/change.js doesn't hand back usable section HTML —
  // it renders each section straight off the cart URL instead, so the cart view
  // still ends up matching server state.
  renderSectionsFromServer(sectionsToRender) {
    return Promise.all(
      sectionsToRender.map((section) =>
        fetch(`${routes.cart_url}?section_id=${section.section}`)
          .then((response) => (response.ok ? response.text() : null))
          .then((html) => ({ section, html })),
      ),
    )
      .then((results) => {
        results.forEach(({ section, html }) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          const innerHTML = this.getSectionInnerHTML(html, section.selector);
          if (innerHTML === null) return;

          const elementToReplace =
            sectionElement.querySelector(section.selector) || sectionElement;
          elementToReplace.innerHTML = innerHTML;
        });

        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      })
      .catch((error) => {
        console.error('Cart section refresh error:', error);
      });
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return element ? element.innerHTML : null;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    // Disable controls while loading (don't hide title since it gets replaced)
    const controls = this.querySelectorAll(
      `#CartItem-${line} .cart-item__controls, #CartDrawer-Item-${line} .cart-item__controls`,
    );
    controls.forEach((control) =>
      control.classList.add('opacity-50', 'pointer-events-none'),
    );

    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    // Re-enable controls after loading (title is already replaced from server)
    const controls = this.querySelectorAll(
      `#CartItem-${line} .cart-item__controls, #CartDrawer-Item-${line} .cart-item__controls`,
    );
    controls.forEach((control) =>
      control.classList.remove('opacity-50', 'pointer-events-none'),
    );
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
